import React from 'react';
/**
 * UniversalStrategyCard — 4 algemene leerstrategieën die altijd waarde toevoegen.
 *
 * Verschijnt als hoofd-inhoud in de empty-state van `SubSkillCheatSheet`
 * wanneer een family geen hints heeft. Geeft het kind altijd iets om mee
 * verder te gaan — meta-cognitief, universeel inzetbaar, en nooit het
 * antwoord weg.
 *
 * Co-located met SubSkillCheatSheet in support/components/ — onderdeel van
 * de support-renderlaag (RC Ronde 5, S-3: verhuisd uit practice/components/).
 */
import { motion } from 'framer-motion';
interface Strategy {
  emoji: string;
  title: string;
  body: string;
}
const STRATEGIES: Strategy[] = [{
  emoji: '📖',
  title: 'Lees de vraag nog eens rustig',
  body: 'Wat staat er precies? Welke woorden zijn belangrijk?'
}, {
  emoji: '💭',
  title: 'Wat weet je al?',
  body: 'Heb je iets vergelijkbaars eerder geleerd?'
}, {
  emoji: '✏️',
  title: 'Schrijf op wat je weet',
  body: 'Pen en papier helpen je hersenen.'
}, {
  emoji: '🎯',
  title: 'Doe een poging',
  body: 'Fout? Geen probleem — de uitleg na de vraag laat zien hoe het werkt.'
}];
interface UniversalStrategyCardProps {
  /** When true, renders a more compact header ("Ook handig:") for use as a secondary footer. */
  variant?: 'primary' | 'secondary';
}
export function UniversalStrategyCard({
  variant = 'primary'
}: UniversalStrategyCardProps) {
  const isSecondary = variant === 'secondary';
  return <section aria-label="Algemene leerstrategieën" className={isSecondary ? 'mt-6 pt-5 border-t border-slate-200/70' : 'mt-2'} data-id="element-11332">
      <h3 className={isSecondary ? 'text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-3' : 'text-sm font-bold text-slate-700 mb-3'} data-id="element-11333">
        {isSecondary ? 'Ook handig' : 'Probeer dit altijd'}
      </h3>
      <ol className="space-y-2.5" data-id="element-11334">
        {STRATEGIES.map((s, i) => <motion.li key={s.title} initial={{
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: i * 0.04,
        duration: 0.18
      }} className="flex items-start gap-3 bg-white rounded-xl border border-slate-200 p-3 shadow-sm" data-id="element-11335">
            <span aria-hidden="true" className="text-xl leading-none mt-0.5 flex-shrink-0" data-id="element-11336">
              {s.emoji}
            </span>
            <div className="min-w-0" data-id="element-11337">
              <p className="text-sm font-semibold text-slate-800 leading-snug" data-id="element-11338">
                {s.title}
              </p>
              <p className="text-xs text-slate-500 mt-0.5 leading-relaxed" data-id="element-11339">
                {s.body}
              </p>
            </div>
          </motion.li>)}
      </ol>
    </section>;
}